// Ask for petrol for a driver's car, paid by eWallet or into a bank account,
// or (kind 'rebate', db/migrations/8001) pay back a station for fuel a driver
// already poured when the Fuel Easy card was refused. A rebate needs the fuel
// slip, so its success screen asks for it at once: it is only sent to Luyanda
// for approval once the slip is filed.
//
// Choosing the driver fills in the car they last drove and their cell number;
// both can be changed. Nothing is signed: petrol is Fleetly's fuel spend for
// that car, never a loan and never a deduction (driver contract 12.2.5, 13.6).
// Postgres checks every field and writes the refusal the manager reads.
import { useEffect, useRef, useState } from 'react'
import { Check, Fuel, Upload } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../lib/shadcn/dialog'
import { Input } from '../../lib/shadcn/input'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { useCreatePetrolRequest } from '../../hooks/backend/petrol'
import { formatZar, localPhone } from '../../utils/loans'
import { DOCUMENT_ACCEPT } from '../../utils/signedUpload'
import { usePaymentFileUpload } from '../payments/PaymentFiles'
import type { PetrolKind, PetrolOptions } from '../../utils/petrol'

type Saved = { ok: boolean; id: string; ref: string; needs_receipt?: boolean }

export function RaisePetrolRequestDialog({
  open, onOpenChange, options, onSaved, kind = 'advance',
}: {
  kind?: PetrolKind
  open: boolean
  onOpenChange: (o: boolean) => void
  options: PetrolOptions | null
  onSaved: () => void
}) {
  const [driverId, setDriverId] = useState('')
  const [vehicleId, setVehicleId] = useState('')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [forDate, setForDate] = useState('')
  const [method, setMethod] = useState<'ewallet' | 'bank'>('ewallet')
  const [ewallet, setEwallet] = useState('')
  const [bank, setBank] = useState('')
  const [holder, setHolder] = useState('')
  const [account, setAccount] = useState('')
  const [branch, setBranch] = useState('')
  const [station, setStation] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<Saved | null>(null)
  const [slipFiled, setSlipFiled] = useState(false)
  const create = useCreatePetrolRequest<Saved>()
  const slip = usePaymentFileUpload()
  const slipInput = useRef<HTMLInputElement>(null)
  const rebate = kind === 'rebate'

  // Reset only when the dialog opens. The page refetches its options when the
  // window regains focus, and keying on the options object would wipe what the
  // manager typed (the Academy dialog met exactly this, 30 September).
  useEffect(() => {
    if (!open) return
    setDriverId(''); setVehicleId(''); setAmount(''); setReason('')
    setForDate(rebate ? '' : options?.today ?? ''); setMethod(rebate ? 'bank' : 'ewallet'); setEwallet('')
    setBank(''); setHolder(''); setAccount(''); setBranch(''); setStation('')
    setError(null); setSaved(null); setSlipFiled(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // A rebate is owed whether or not the driver still works here, so it lists
  // everyone who drove in the rebate window; petrol for a shift lists only the
  // active drivers, as before.
  const drivers = (options?.drivers ?? []).filter((d) => rebate || d.status === 'active')
  const vehicles = options?.vehicles ?? []

  function chooseDriver(id: string) {
    setDriverId(id)
    const d = drivers.find((x) => x.id === id)
    if (!d) return
    if (d.last_vehicle_id && vehicles.some((v) => v.id === d.last_vehicle_id)) setVehicleId(d.last_vehicle_id)
    // The payee of a rebate is the station, so nothing of the driver's is filled in.
    if (rebate) return
    setEwallet(localPhone(d.phone))
    setHolder(d.name)
  }

  async function fileSlip(file: File | undefined) {
    if (!file || !saved) return
    setError(null)
    try {
      await slip.upload('petrol', saved.id, 'receipt', file)
      setSlipFiled(true)
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      if (slipInput.current) slipInput.current.value = ''
    }
  }

  async function save() {
    setError(null)
    try {
      const r = await create.trigger({
        driver_id: driverId || null,
        vehicle_id: vehicleId || null,
        amount_zar: amount.replace(',', '.').trim(),
        reason,
        for_date: forDate || null,
        payout_method: method,
        ewallet_number: method === 'ewallet' ? ewallet : null,
        bank_name: method === 'bank' ? bank : null,
        account_holder: method === 'bank' ? holder : null,
        account_number: method === 'bank' ? account : null,
        branch_code: method === 'bank' ? branch : null,
        kind,
        station_name: rebate ? station : null,
      })
      if (r) { setSaved(r); onSaved() }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  if (saved && rebate) {
    const who = options?.releasers ?? 'Luyanda'
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!slip.busy) onOpenChange(o) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{saved.ref} is saved. {slipFiled ? `It is with ${who}.` : 'Now the fuel slip.'}</DialogTitle>
            <DialogDescription>
              {slipFiled
                ? `The slip is filed and ${who} has been emailed to approve and pay it. You will be emailed when it is paid or declined.`
                : `A rebate goes to ${who} only once the slip from the station is filed, because the slip is what shows the fuel went into the car. A photo of it is fine.`}
            </DialogDescription>
          </DialogHeader>
          <input ref={slipInput} type="file" accept={DOCUMENT_ACCEPT} className="hidden" onChange={(e) => void fileSlip(e.target.files?.[0])} />
          {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
          <div className="flex justify-end gap-2">
            {slipFiled ? (
              <Button variant="brand" onClick={() => onOpenChange(false)}><Check className="mr-1 h-4 w-4" /> Done</Button>
            ) : (
              <>
                <Button variant="ghost" disabled={slip.busy} onClick={() => onOpenChange(false)}>Later</Button>
                <Button variant="brand" disabled={slip.busy} onClick={() => slipInput.current?.click()}>
                  {slip.busy ? <Spinner className="mr-1 h-4 w-4" /> : <Upload className="mr-1 h-4 w-4" />} Upload the fuel slip
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  if (saved) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{saved.ref} is with {options?.releasers ?? 'Luyanda'}</DialogTitle>
            <DialogDescription>
              {options?.releasers ?? 'Luyanda'} has been emailed to approve and pay it. You will be emailed when it is paid or declined, and it shows here too.
              Do not send the driver to the pump until it shows paid.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end"><Button variant="brand" onClick={() => onOpenChange(false)}>Done</Button></div>
        </DialogContent>
      </Dialog>
    )
  }

  const max = options?.max_zar ?? 0

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!create.loading) onOpenChange(o) }}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{rebate ? 'Pay back a station' : 'Request petrol for a driver'}</DialogTitle>
          <DialogDescription>
            {rebate
              ? `For fuel a driver already poured when the Fuel Easy card was refused. You will upload the slip next, and ${options?.releasers ?? 'Luyanda'} pays the station.`
              : `${options?.releasers ?? 'Luyanda'} approves and pays it.`}{' '}
            It counts as fuel spend for the car, never as money the driver owes.
            {max > 0 ? ` Up to ${formatZar(max)} in one request.` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="space-y-1">
            <label htmlFor="pr-driver" className="text-xs font-medium text-foreground">Driver</label>
            <NativeSelect id="pr-driver" value={driverId} onChange={(e) => chooseDriver(e.target.value)}>
              <option value="">Choose the driver</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.status !== 'active' ? `, ${d.status}` : d.manager ? ` (${d.manager})` : ''}</option>)}
            </NativeSelect>
          </div>
          <div className="space-y-1">
            <label htmlFor="pr-vehicle" className="text-xs font-medium text-foreground">{rebate ? 'Car the petrol went into' : 'Car the petrol goes into'}</label>
            <NativeSelect id="pr-vehicle" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              <option value="">Choose the car</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </NativeSelect>
            <p className="text-[11px] text-muted-foreground">Filled with the car they last drove. Change it if they are in another one.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="pr-amount" className="text-xs font-medium text-foreground">Amount (R)</label>
              <Input id="pr-amount" inputMode="decimal" placeholder={rebate ? 'As on the slip' : '400'} value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label htmlFor="pr-date" className="text-xs font-medium text-foreground">{rebate ? 'Day on the slip' : 'Shift date'}</label>
              <Input id="pr-date" type="date" max={rebate ? options?.today : undefined} value={forDate} onChange={(e) => setForDate(e.target.value)} />
            </div>
          </div>
          {rebate && (
            <div className="space-y-1">
              <label htmlFor="pr-station" className="text-xs font-medium text-foreground">Station</label>
              <Input id="pr-station" placeholder="e.g. Sasol Midrand" value={station} onChange={(e) => setStation(e.target.value)} />
            </div>
          )}
          <div className="space-y-1">
            <label htmlFor="pr-reason" className="text-xs font-medium text-foreground">{rebate ? 'What happened' : 'Why it is needed'}</label>
            <Input id="pr-reason" placeholder={rebate ? 'e.g. Fuel Easy card declined at the pump, station let him fill' : 'e.g. Tank empty at the start of the shift'} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>

          <fieldset className="space-y-2 rounded-md border border-border p-3">
            <legend className="px-1 text-xs font-medium text-foreground">How {options?.releasers ?? 'Luyanda'} pays it</legend>
            <div className="flex flex-wrap gap-4 text-xs">
              <label className="flex items-center gap-2"><input type="radio" name="pr-payout" checked={method === 'ewallet'} onChange={() => setMethod('ewallet')} /> eWallet to a cell number</label>
              <label className="flex items-center gap-2"><input type="radio" name="pr-payout" checked={method === 'bank'} onChange={() => setMethod('bank')} /> Into a bank account</label>
            </div>
            {method === 'ewallet' ? (
              <div className="space-y-1">
                <label htmlFor="pr-ewallet" className="text-xs font-medium text-foreground">Cell number for the eWallet</label>
                <Input id="pr-ewallet" inputMode="tel" placeholder="0821234567" value={ewallet} onChange={(e) => setEwallet(e.target.value)} />
              </div>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-1">
                  <label htmlFor="pr-bank" className="text-xs font-medium text-foreground">Bank</label>
                  <NativeSelect id="pr-bank" value={bank} onChange={(e) => setBank(e.target.value)}>
                    <option value="">Choose the bank</option>
                    {(options?.banks ?? []).map((b) => <option key={b} value={b}>{b}</option>)}
                  </NativeSelect>
                </div>
                <div className="space-y-1">
                  <label htmlFor="pr-holder" className="text-xs font-medium text-foreground">Account in the name of</label>
                  <Input id="pr-holder" placeholder={rebate ? 'The station or its owner' : undefined} value={holder} onChange={(e) => setHolder(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label htmlFor="pr-account" className="text-xs font-medium text-foreground">Account number</label>
                  <Input id="pr-account" inputMode="numeric" value={account} onChange={(e) => setAccount(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label htmlFor="pr-branch" className="text-xs font-medium text-foreground">Branch code (optional)</label>
                  <Input id="pr-branch" inputMode="numeric" value={branch} onChange={(e) => setBranch(e.target.value)} />
                </div>
              </div>
            )}
          </fieldset>

          {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.loading}>Cancel</Button>
            <Button variant="brand" onClick={() => void save()} disabled={create.loading}>
              {create.loading ? <Spinner className="mr-1 h-4 w-4" /> : <Fuel className="mr-1 h-4 w-4" />} {rebate ? 'Save, then upload the slip' : `Send to ${options?.releasers ?? 'Luyanda'}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
